import type { RotaPatternResponse } from '@lmc/ts-common'
import { Descriptions, Heading } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { toLower } from 'lodash'
import { useTranslation } from 'react-i18next'
import useUserSkills from 'screens/Rostering/hooks/useUserSkills'
import styled from 'styled-components'

type TeamMembersAndSkillsProps = {
  pattern?: RotaPatternResponse
}

const TeamMembersAndSkills = ({ pattern }: TeamMembersAndSkillsProps) => {
  const { t } = useTranslation()

  const { allSkills } = useUserSkills()

  if (!pattern) return null

  const list = [
    {
      key: `total`,
      label: <Text>{t('TOTAL_NUMBER_REQUIRED')}</Text>,
      children: (
        <Text>{`${pattern.numberSupply} ${toLower(t('TEAM_MEMBERS'))}`}</Text>
      )
    },
    ...(pattern.skills?.map(
      (skillNeeded: { skillId: string; skillSupplyNumber: number }) => {
        return {
          key: `skill`,
          label: (
            <Text>
              {
                allSkills.find((skill) => skill?.id === skillNeeded?.skillId)
                  ?.name
              }
            </Text>
          ),
          children: (
            <Text>{`${skillNeeded?.skillSupplyNumber} ${toLower(
              t('TEAM_MEMBERS')
            )}`}</Text>
          )
        }
      }
    ) ?? [])
  ]

  return (
    <MainWrapper>
      <TopWrapper>
        <InfoWrapper>
          <TitlesWrapper>
            <Heading level={3}>{t('TEAM_MEMBERS_AND_SKILLS')}</Heading>
          </TitlesWrapper>
        </InfoWrapper>
      </TopWrapper>
      <DescWrapper>
        <Descriptions
          labelStyle={labelStyle}
          items={list}
          bordered
          column={1}
          layout="horizontal"
        />
      </DescWrapper>
    </MainWrapper>
  )
}

const labelStyle = { width: 150 }

const TitlesWrapper = styled.div`
  margin-left: 8px;
  display: flex;
  flex-direction: column;
`

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.color.neutral.color11};
  padding: 24px;
  border-radius: ${(props) => props.theme.properties.borderRadius.large};
  flex: 1;
`

const TopWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const DescWrapper = styled.div`
  border-top: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color8};
  padding-top: 16px;
  margin-top: 16px;
`

export default TeamMembersAndSkills
